import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatQuarterRange } from "../../../utils/kpiColors";

const KPIReports = () => {
  const dispatch = useDispatch();
  const { trend, categoryBreakdown, dashboard } = useSelector((state) => state.kpi);

  const [trendPeriod, setTrendPeriod] = React.useState("month");

  const handlePeriodChange = (period) => {
    setTrendPeriod(period);
    dispatch.kpi.getTrend({ period });
  };

  useEffect(() => {
    dispatch.kpi.getTrend({ period: trendPeriod });
    dispatch.kpi.getCategoryBreakdown();
    dispatch.kpi.getDashboard();
  }, [dispatch]);

  const summary = dashboard?.summary || {};
  const categoryData = Array.isArray(categoryBreakdown) ? categoryBreakdown : [];
  const rawTrend = (Array.isArray(trend) && trend.length > 0)
    ? trend
    : (dashboard?.trend || []);

  const trendData = rawTrend.map((item, i) => {
    const qInfo = formatQuarterRange(item, i, trendPeriod);
    let label = item.label || (item.day && item.month ? `${item.day} ${item.month}` : (item.month || item.name || `P${i+1}`));
    if (qInfo) {
      label = qInfo.range;
    }
    return {
      ...item,
      label,
      score: item.score !== undefined && item.score !== null ? item.score : 0,
    };
  });

  const totalPoints = summary.total_points !== undefined ? summary.total_points : 0;
  const completedCount = summary.completed !== undefined ? summary.completed : 0;

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Reports</h1>
          <p className="text-slate-500 text-sm">
            Generate and export detailed KPI reports across periods
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
            Last 12 months
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            Export Report
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Total KPI Score</div>
          <div className="flex items-baseline gap-3">
            <div className="text-4xl font-light text-slate-900">{totalPoints}</div>
            <div className="text-sm font-semibold text-emerald-500">
              {summary.score_change_direction === 'DECREASE' ? `${summary.score_change || 0}%` : `+${summary.score_change || 0}%`}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Works Completed</div>
          <div className="flex items-baseline gap-3">
            <div className="text-4xl font-light text-slate-900">{completedCount}</div>
            <div className="text-sm font-semibold text-emerald-500">({summary.completed_percentage || 0}%)</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Active Works</div>
          <div className="flex items-baseline gap-3">
            <div className="text-4xl font-light text-slate-900">{summary.in_progress || 0}</div>
            <div className="text-sm font-semibold text-amber-500">({summary.in_progress_percentage || 0}%)</div>
          </div>
        </div>
      </div>

      {/* 12-Month Score Trend */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">KPI Score Trend</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {trendPeriod === "month"
                ? "Monthly scores · trend analysis"
                : trendPeriod === "quarter"
                ? "Quarterly scores · trend analysis"
                : "Yearly scores · trend analysis"}
            </p>
          </div>
          <div className="flex items-center p-1 bg-slate-100 rounded-xl">
            <button
              type="button"
              onClick={() => handlePeriodChange("month")}
              className={`px-3 py-1 text-xs transition-all duration-150 ${
                trendPeriod === "month"
                  ? "bg-white text-slate-900 rounded-lg shadow-2xs font-bold"
                  : "font-medium text-slate-500 hover:text-slate-900"
              }`}
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => handlePeriodChange("quarter")}
              className={`px-3 py-1 text-xs transition-all duration-150 ${
                trendPeriod === "quarter"
                  ? "bg-white text-slate-900 rounded-lg shadow-2xs font-bold"
                  : "font-medium text-slate-500 hover:text-slate-900"
              }`}
            >
              Quarter
            </button>
            <button
              type="button"
              onClick={() => handlePeriodChange("year")}
              className={`px-3 py-1 text-xs transition-all duration-150 ${
                trendPeriod === "year"
                  ? "bg-white text-slate-900 rounded-lg shadow-2xs font-bold"
                  : "font-medium text-slate-500 hover:text-slate-900"
              }`}
            >
              Year
            </button>
          </div>
        </div>
        
        {trendData.length > 0 ? (
          <div className="h-64 flex items-end gap-2 pb-6 border-b border-slate-100 relative pt-4">
            <div className="flex-1 flex justify-between items-end pl-4 pr-2 h-full">
              {trendData.map((d, i) => {
                const score = d.score || 0;
                const maxScore = Math.max(...trendData.map(t => t.score || 100), 100);
                const heightPct = Math.min(100, Math.max(10, (score / maxScore) * 100));

                return (
                  <div key={i} className="flex flex-col items-center gap-2 group flex-1">
                    <div className="text-[10px] font-bold text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">
                      {score}
                    </div>
                    <div className="flex items-end justify-center w-full h-44">
                      <div 
                        className="w-full max-w-[28px] bg-slate-900 rounded-t-md hover:bg-slate-800 transition-all shadow-xs" 
                        style={{ height: `${heightPct}%` }}
                      ></div>
                    </div>
                    <div className="text-[10px] text-slate-500 font-medium">{d.label || d.month || d.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-slate-400 italic">No score trend data available.</div>
        )}
      </div>

      {/* Category Breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-4">Category Breakdown</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categoryData.length > 0 ? (
            categoryData.map((cat, idx) => (
              <div key={idx} className="p-4 rounded-xl border border-slate-100 bg-slate-50/50 flex justify-between items-center">
                <div>
                  <div className="text-sm font-bold text-slate-900">{cat.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Assigned points</div>
                </div>
                <div className="text-lg font-extrabold text-slate-900">{cat.points || 0} pts</div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-8 text-center text-sm text-slate-400 italic">
              No category breakdown data available.
            </div>
          )}
        </div>
      </div>

      {/* Saved Reports */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Saved reports</h2>
            <p className="text-sm text-slate-500 mt-0.5">Auto-generated weekly · shared with HR</p>
          </div>
          <button className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            New
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {Array.isArray(dashboard?.saved_reports || dashboard?.reports) && (dashboard?.saved_reports || dashboard?.reports).length > 0 ? (
            (dashboard?.saved_reports || dashboard?.reports).map((r, i) => (
              <div key={i} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors group">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{r.title || r.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{r.meta || r.description || "Report"}</div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-xs font-mono text-slate-500">{r.date || r.created_at || ""}</div>
                  <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                    Open
                  </button>
                  <button className="text-slate-400 hover:text-slate-600">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center text-sm text-slate-400 font-medium">
              No saved reports available.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KPIReports;
